import React, { useEffect, useState } from 'react';
import debounce from 'debounce';
import { Form, Select, AutoComplete, DatePicker, Tooltip } from 'antd';
import { SearchOutlined, UndoOutlined, FireTwoTone, StarTwoTone } from '@ant-design/icons';
import Input from '../../components/Input';
import FilterItem from './FilterItem';
import setFirstPage from '../../section/Common/utils/setFirstPage';
import './styles.css';
import {
  ALL_NEW_TITLE,
  TOP_POD_TITLES,
  BOOKSTAT_DATA,
  PODIUM_SALES,
  WATCHLIST,
  RISING_STARS,
  AUTHORS,
  PENDING_EXCLUSION,
  PODIUMAUTHORS,
  CONTACTED,
  MUTED_AUTHORS_RR
} from '../../core/constants';
import FilterItemTag from './FilterItemTag';
import styled from 'styled-components';
import JobStatus from '../JobStatus/JobStatus';
import SaveSearchButton from '../SavedSearches/SaveSearchButton';
import { useFilter } from '../../context/Filter.context';
import { defaultSort, useSort } from '../../context/Sort.context';
import { useActions } from '../../context/Actions.context';
import { useHistory } from 'react-router-dom';
import { useBooksList, useDataRowCount } from '../../core/modules/book/hooks';
import {
  allowedFiltersPerTab,
  AO3Tabs,
  DAILY_RANK_DATE_FORMAT,
  DailyRankTabs,
  KUDOS_THRESHOLD,
  RoyalRoadTabs
} from '../../section/Common/constants';
import sendGAFilterEvent from '../../section/Common/utils/gaFilterEvents';
import moment from 'moment';
import useConfig from '../../core/modules/book/hooks/useConfig.query';
import { getDateRangeFormValues } from '../../section/Common/utils/getDate';

const { Option } = Select;

const StyledReset = styled.button`
  border: none;
  outline: none;
  background: none;
  color: #9c9a9a;
  font-weight: 600;
  margin: 0;
  cursor: pointer;
  svg {
    width: 26px;
    height: 26px;
    transform: rotate(45deg);
  }
  &:hover {
    color: #00b27b;
  }
`;

const selectProps = {
  mode: 'multiple' as const,
  className: 'filter-select',
  showArrow: true,
  allowClear: true
};

interface IProps {
  page: number;
  changePage: (newPage: number) => void;
  id: any;
  selectedOption?: string;
  setSelectedOption?: React.Dispatch<React.SetStateAction<any>>;
  dataRefreshTime?: string;
  isBackendJobComplete?: boolean;
}

const FilterForm = ({ page, changePage, id, selectedOption,
  setSelectedOption, dataRefreshTime, isBackendJobComplete }: IProps) => {
  const [form] = Form.useForm();
  const { filter, setFilter } = useFilter();
  const { sort, setSort } = useSort();
  const { source: sourcePath, setIsRefreshed } = useActions();
  const history = useHistory();
  const { refetch: refetchData } = useBooksList(id, { page, runBy: 'BrendaCaching' });
  const { refetch: refetchDataRowCount } = useDataRowCount(id, { runBy: 'BrendaCaching' });
  const { RangePicker } = DatePicker;
  const [rangePickerKey, setRangePickerKey] = useState(0);
  const { data: config } = useConfig();
  const kudosThreshold = config?.ao3?.kudos_threshold ?? KUDOS_THRESHOLD;
  const [kudosValue, setKudosValue] = useState<number | undefined>();
  const isInvalid = kudosValue !== undefined &&
    kudosValue < kudosThreshold;

  const handleDatePickerRemount = () => {
    setRangePickerKey(prevKey => prevKey + 1);
  };

  const defaultStartDateRange = filter[id]?.start_date ? moment(filter[id].start_date) : null;
  const defaultEndDateRange = filter[id]?.end_date ? moment(filter[id].end_date) : null;
  const defaultPublishedStartDate = filter[id]?.published_start_date ? moment(filter[id].published_start_date)
    : null;
  const defaultPublishedEndDate = filter[id]?.published_end_date ? moment(filter[id].published_end_date)
    : null;
  const defaultLastUpdatedStartDate = filter[id]?.last_updated_start_date ? moment(filter[id].last_updated_start_date)
    : null;
  const defaultLastUpdatedEndDate = filter[id]?.last_updated_end_date ? moment(filter[id].last_updated_end_date)
    : null;

  const getFilterData = debounce((a, values: { [x: string]: any; }) => {
    const kudos = values[`${id}kudos`];
    const kudosNum = kudos !== undefined && kudos !== '' ? Number(kudos) : undefined;

    setKudosValue(kudosNum);
    const kudosInvalid = kudosNum !== undefined && kudosNum < kudosThreshold;

    if (kudosInvalid) {
      return;
    }

    const params = {};

    for (const key in values) {
      if (values[key] instanceof Array) {
        params[key.replace(id, '')] = values[key].map(
          (value: string) => value.replace(/\s{2,}/g, ' ').trim()
        );
      } else {
        params[key.replace(id, '')] = key === `${id}search` ?
          values[key].replace(/\s{2,}/g, ' ').trim() :
          values[key];
      }
    }

    const getFilteredParamsForTab = (
      params_: Record<string, any>,
      filterId: string
    ) => {
      const allowedFilters = allowedFiltersPerTab[filterId] || [];

      return Object.keys(params_).reduce((validParams, key) => {
        if (allowedFilters.includes(key)) {
          validParams[key] = params_[key];
        }
        return validParams;
      }, {} as Record<string, any>);
    };

    const buildUpdatedFilters = (
      prevFilter: Record<string, any>,
      params_: Record<string, any>
    ) => Object.keys(prevFilter).reduce((acc, filterId) => {
      const filteredParams = getFilteredParamsForTab(params_, filterId);

      acc[filterId] = {
        ...prevFilter[filterId],
        ...filteredParams
      };

      return acc;
    }, {} as Record<string, any>);

    setFilter(prevFilter => buildUpdatedFilters(prevFilter, params));
    setFirstPage(page, changePage);
    sendGAFilterEvent(a, sourcePath['source'], id);
  }, 2000);

  const onReset = async () => {
    setIsRefreshed(true);
    history.push({ search: '' });
    handleDatePickerRemount();
    setFilter({
      ...filter,
      [id]: {
        amazonasin: [],
        genres: [],
        search: '',
        tag: [],
        not_tag: [],
        days_tracked: [],
        status_tag: [],
        type_tag: [],
        reputation: [],
        author_tags: [],
        fiction_tags: [],
        warning_tags: [],
        not_warning_tags: [],
        genre_categories: [],
        date_range: undefined,
        start_date: undefined,
        end_date: undefined,
        min_rank: undefined,
        max_rank: undefined,
        is_complete: [],
        hits: undefined,
        kudos: undefined,
        published_start_date: undefined,
        published_end_date: undefined,
        last_updated_start_date: undefined,
        last_updated_end_date: undefined
      }
    });
    setKudosValue(undefined);
    setFirstPage(page, changePage);
    setTimeout(() => form.resetFields());
    setSort(defaultSort);
  };

  const initialValues = {
    [`${id}search`]: filter[id]?.search,
    [`${id}amazonasin`]: filter[id]?.amazonasin,
    [`${id}genres`]: filter[id]?.genres,
    [`${id}tag`]: filter[id]?.tag,
    [`${id}not_tag`]: filter[id]?.not_tag,
    [`${id}days_tracked`]: filter[id]?.days_tracked,
    [`${id}status_tag`]: filter[id]?.status_tag,
    [`${id}type_tag`]: filter[id]?.type_tag,
    [`${id}reputation`]: filter[id]?.reputation,
    [`${id}author_tags`]: filter[id]?.author_tags,
    [`${id}fiction_tags`]: filter[id]?.fiction_tags,
    [`${id}warning_tags`]: filter[id]?.warning_tags,
    [`${id}not_warning_tags`]: filter[id]?.not_warning_tags,
    [`${id}genre_categories`]: filter[id]?.genre_categories,
    [`${id}date_range`]: filter[id]?.date_range,
    [`${id}late_bloomer`]: filter[id]?.late_bloomer,
    [`${id}start_date`]: filter[id]?.start_date,
    [`${id}end_date`]: filter[id]?.end_date,
    [`${id}min_rank`]: filter[id]?.min_rank,
    [`${id}max_rank`]: filter[id]?.max_rank,
    [`${id}fandom`]: filter[id]?.fandom,
    [`${id}is_complete`]: filter[id]?.is_complete,
    [`${id}hits`]: filter[id]?.hits,
    [`${id}kudos`]: filter[id]?.kudos,
    [`${id}published_start_date`]: filter[id]?.published_start_date,
    [`${id}published_end_date`]: filter[id]?.published_end_date,
    [`${id}last_updated_start_date`]: filter[id]?.last_updated_start_date,
    [`${id}last_updated_end_date`]: filter[id]?.last_updated_end_date
  };

  useEffect(() => {
    form.resetFields();
  }, [filter[id]]);

  useEffect(() => {
    if (filter[id]?.option && setSelectedOption) {
      setSelectedOption(filter[id]?.option);
    }
  }, [filter[id]?.option]);

  const handleReset = async () => {
    await onReset();
    refetchDataRowCount();
    refetchData();
    setIsRefreshed(false);
  };

  const handleDateRangeChange = (dates, fields: { startField: string; endField: string; extraField?: string }) => {
    form.setFieldsValue(getDateRangeFormValues(dates, fields));

    if (!dates) {
      getFilterData({}, form.getFieldsValue());
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Form
        className='filter'
        form={form}
        onKeyPress={e => {
          if (e.code === 'Enter') {
            e.preventDefault();
          }
        }}
        onValuesChange={getFilterData}
        initialValues={initialValues}
      >
        <div className='filter-control-select'>
          <Form.Item name={`${id}search`}>
            <Input className='filter-input' prefix={<SearchOutlined />} />
          </Form.Item>
          {(id !== AUTHORS && id !== PODIUMAUTHORS &&
            !DailyRankTabs.includes(id) &&
            !RoyalRoadTabs.includes(id) &&
            !AO3Tabs.includes(id)) &&
            (<><FilterItem
              selectProps={selectProps}
              point='amazonasin'
              placeholder='ASINs'
              id={id}
            />
            {id !== PODIUM_SALES && id !== TOP_POD_TITLES && id !== MUTED_AUTHORS_RR && (
              <FilterItem
                selectProps={selectProps}
                point='genres'
                placeholder='Genres'
                id={id}
              />
            )}
            {(id !== PODIUM_SALES && id !== BOOKSTAT_DATA && id !== TOP_POD_TITLES) &&
                (<FilterItemTag
                  selectProps={selectProps}
                  point='tag'
                  placeholder='Tags'
                  id={id}
                />
                )}
            {id !== TOP_POD_TITLES && (
              <Form.Item name={`${id}days_tracked`}>
                <Select
                  placeholder='Days Tracked'
                  className='filter-select'
                  showArrow
                  allowClear
                  style={{ width: '100%' }}
                >
                  <Option value='120'>Less than 120 days</Option>
                  <Option value='90'>Less than 90 days</Option>
                  <Option value='60'>Less than 60 days</Option>
                  <Option value='30'>Less than 30 days</Option>
                </Select>
              </Form.Item>
            )}
            </>)}
          {RoyalRoadTabs.includes(id) && id !== AUTHORS && id !== PODIUMAUTHORS && (
            <><FilterItemTag
              selectProps={selectProps}
              point='warning_tags'
              placeholder='Tags'
              id={id}
            />
            <FilterItem
              selectProps={selectProps}
              point='fiction_tags'
              placeholder='Genres'
              id={id}
            />
            <Form.Item name={`${id}status_tag`}>
              <Select
                placeholder='Status'
                className='filter-select'
                showArrow
                allowClear
                style={{ width: '100%' }}
                mode={'multiple'}
              >
                <Option value='COMPLETED'>Completed</Option>
                <Option value='DROPPED'>Dropped</Option>
                <Option value='HIATUS'>Hiatus</Option>
                <Option value='ONGOING'>Ongoing</Option>
                <Option value='STUB'>Stub</Option>
              </Select>
            </Form.Item>
            <Form.Item name={`${id}type_tag`}>
              <Select
                placeholder='Type'
                className='filter-select'
                showArrow
                allowClear
                style={{ width: '100%' }}
              >
                <Option value='Original'>Original</Option>
                <Option value='Fan Fiction'>Fan Fiction</Option>
              </Select>
            </Form.Item>
            </>)}
          {(id === AUTHORS || id === PODIUMAUTHORS) &&
            (<><Form.Item name={`${id}reputation`}>
              <Select
                placeholder='Reputation Level'
                className='filter-select'
                showArrow
                allowClear
                tokenSeparators={[',']}
                style={{ width: '100%' }}
                mode={'tags'}
              >
                {
                  [...Array(100)].map((_, i) => i + 1).map((val) =>
                    <Option value={`${val}`} key={`option-${val}`}>{val}</Option>)
                }
              </Select>
            </Form.Item>
            <Form.Item name={`${id}author_tags`}>
              <Select
                placeholder='Authors Tag'
                className='filter-select'
                showArrow
                allowClear
                style={{ width: '100%' }}
                mode={'multiple'}
              >
                <Option value='watchlist'>Watchlist</Option>
                <Option value='excluded'>Excluded</Option>
              </Select>
            </Form.Item></>)}
          {DailyRankTabs.includes(id) && (
            <>
              <FilterItem
                selectProps={selectProps}
                point='genre_categories'
                placeholder='Genre Categories'
                id={id}
              />
              <Form.Item shouldUpdate>
                {() => <Form.Item name={`${id}date_range`} >
                  <AutoComplete
                    className='filter-select'
                    placeholder='Date Range'
                    showArrow
                    allowClear
                    disabled={form.getFieldValue(`${id}start_date`)}
                  >
                    {
                      [...Array(11)].map((_, i) => i * 30 + 30).map((val) =>
                        <Option value={val} key={`option-${val}`}>{val} days</Option>)
                    }
                    <Option value={365}>365 days</Option>
                  </AutoComplete>
                </Form.Item >}
              </Form.Item>
              <RangePicker
                key={rangePickerKey}
                className='date-range-picker'
                format={DAILY_RANK_DATE_FORMAT}
                defaultValue={[defaultStartDateRange, defaultEndDateRange]}
                onCalendarChange={(dates) =>
                  handleDateRangeChange(dates, {
                    startField: `${id}start_date`,
                    endField: `${id}end_date`,
                    extraField: `${id}date_range`
                  })
                }
                onOpenChange={(open) => {
                  if (!open && form.getFieldValue(`${id}start_date`)) {
                    getFilterData({ [`${id}date_range`]: 'custom' }, form.getFieldsValue());
                  }
                }}
              />
              <Form.Item name={`${id}start_date`} style={{ display: 'none' }} />
              <Form.Item name={`${id}end_date`} style={{ display: 'none' }} />
              <div className='filter-input-wrapper'>
                <Form.Item name={`${id}min_rank`}>
                  <Input
                    className='filter-input-number'
                    placeholder='Min Rank'
                    type='number'
                    allowClear
                    min={1}
                  />
                </Form.Item>
                <Form.Item name={`${id}max_rank`}>
                  <Input
                    className='filter-input-number'
                    placeholder='Max Rank'
                    type='number'
                    allowClear
                    min={1}
                  />
                </Form.Item>
              </div>
            </>
          )}
          {(id === ALL_NEW_TITLE || id === PENDING_EXCLUSION || id === WATCHLIST || id === CONTACTED) && (
            <Form.Item name={`${id}late_bloomer`}>
              <Select
                placeholder='All Titles'
                className='filter-select'
                showArrow
                allowClear
                style={{ width: '100%' }}
                value={filter[id]['late_bloomer']}
              >
                <Option value={'late_bloomer'}>
                  Show Late Bloomers <FireTwoTone twoToneColor={'red'} />
                </Option>
              </Select>
            </Form.Item>
          )}
          {(RoyalRoadTabs.includes(id) && id !== AUTHORS && id !== RISING_STARS) &&
            (
              <Select
                className='filter-select'
                showArrow
                onChange={(value: string) => {
                  if (setSelectedOption) {
                    setSelectedOption(value);
                  }
                }}
                value={selectedOption}
              >
                <Select.Option value={'All Titles'}>All Titles</Select.Option>
                <Select.Option value={'Rising Stars'}>
                  Show Rising Stars <StarTwoTone twoToneColor={'#F6BE00'} />
                </Select.Option>
              </Select>
            )}
          {AO3Tabs.includes(id) && (
            <>
              <FilterItem
                selectProps={selectProps}
                point='fandom'
                placeholder='Fandom'
                id={id}
              />

              <Form.Item name={`${id}is_complete`}>
                <Select
                  placeholder='Completion Status'
                  className='filter-select'
                  showArrow
                  allowClear
                  style={{ width: '100%' }}
                >
                  <Option value='1'>Completed</Option>
                  <Option value='0'>In Progress</Option>
                </Select>
              </Form.Item>
              <div className='filter-input-wrapper'>
                <Form.Item name={`${id}hits`}>
                  <Input
                    className='filter-input-number'
                    placeholder='Min Hits'
                    type='number'
                    allowClear
                    min={1}
                  />
                </Form.Item>
                <Tooltip
                  visible={isInvalid}
                  title={`Min Kudos must be at least ${kudosThreshold}`}
                  placement='bottom'
                >
                  <Form.Item name={`${id}kudos`}
                  >
                    <Input
                      className='filter-input-number'
                      placeholder='Min Kudos'
                      type='number'
                      allowClear
                    />
                  </Form.Item>
                </Tooltip>
              </div>
              <RangePicker
                className='published-date-range-picker'
                format={DAILY_RANK_DATE_FORMAT}
                placeholder={['Published From', 'Published To']}
                defaultValue={[
                  defaultPublishedStartDate,
                  defaultPublishedEndDate
                ]}
                onCalendarChange={(dates) =>
                  handleDateRangeChange(dates, {
                    startField: `${id}published_start_date`,
                    endField: `${id}published_end_date`
                  })
                }
                onOpenChange={(open) => {
                  if (
                    !open &&
                    form.getFieldValue(`${id}published_start_date`)
                  ) {
                    getFilterData({}, form.getFieldsValue());
                  }
                }}
              />
              <Form.Item name={`${id}published_start_date`}
                style={{ display: 'none' }}
              />
              <Form.Item name={`${id}published_end_date`}
                style={{ display: 'none' }}
              />
              <RangePicker
                className='last-updated-date-range-picker'
                format={DAILY_RANK_DATE_FORMAT}
                placeholder={['Last Updated From', 'Last Updated To']}
                defaultValue={[
                  defaultLastUpdatedStartDate,
                  defaultLastUpdatedEndDate
                ]}
                onCalendarChange={(dates) =>
                  handleDateRangeChange(dates, {
                    startField: `${id}last_updated_start_date`,
                    endField: `${id}last_updated_end_date`
                  })
                }
                onOpenChange={(open) => {
                  if (
                    !open &&
                    form.getFieldValue(`${id}last_updated_start_date`)
                  ) {
                    getFilterData({}, form.getFieldsValue());
                  }
                }}
              />
              <Form.Item name={`${id}last_updated_start_date`}
                style={{ display: 'none' }}
              />
              <Form.Item name={`${id}last_updated_end_date`}
                style={{ display: 'none' }}
              />
            </>)}
        </div>
      </Form>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        {dataRefreshTime && (
          <JobStatus
            dataRefreshTime={dataRefreshTime}
            isBackendJobComplete={isBackendJobComplete}
          />
        )}
        {!AO3Tabs.includes(id) &&
        <SaveSearchButton
          tab={id}
          filters={{ ...filter[id], option: selectedOption }}
          sort={sort[id]}
          source={sourcePath['source']}
        />
        }
        <StyledReset type='submit' onClick={() => { handleReset(); }}>
          <UndoOutlined />
        </StyledReset>
      </div>
    </div>
  );
};

export default React.memo(FilterForm);